import { useCallback } from 'react'
import * as React from 'react'
import clsx from 'clsx'

import LineHeartIcon from 'LuxKit/Icons/line/LineHeartIcon'
import SolidHeartIcon from 'LuxKit/Icons/solid/SolidHeartIcon'
import noop from 'lib/function/noop'
import { getThemeClassName } from 'lib/theme/themeUtils'
import { preventDefault, stopPropagation } from 'lib/web/eventHandlerUtils'
import useShouldShowTripPlannerSaveEntryPoints from 'tripPlanner/hooks/useShouldShowTripPlannerSaveEntryPoints'
import IconButton from 'LuxKit/Button/IconButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'

const IconBookmarkButton = styled(IconButton)`
  &.withlabel-tablet {
    ${mediaQueryUp.tablet} {
      display: none;
    }
  }
`

const ChipBookmarkButton = styled(AssistChip)`
  &.withlabel-tablet {
    display: none;

    ${mediaQueryUp.tablet} {
      display: inline-flex;
    }
  }
`

interface Props extends Utils.PropsWithDataAttributes {
  className?: string
  label?: string
  onClick?: () => void
  isSaved?: boolean
  isProcessing?: boolean
  size?: 'small' | 'medium' | 'large'
  withLabel?: 'never' | 'tablet' | 'always'
  iconKind?: React.ComponentProps<typeof IconButton>['kind']
  iconVariant?: React.ComponentProps<typeof IconButton>['variant']
}

export function BaseBookmarkButtonInner(props: Props) {
  const {
    className,
    label,
    onClick = noop,
    isSaved = false,
    isProcessing = false,
    size = 'medium',
    withLabel: withLabelProp = 'always',
    iconKind = 'secondary',
    iconVariant = 'dark',
    'data-testid': testId,
    ...rest
  } = props

  const withLabel = label ? withLabelProp : 'never'

  const savedIcon = isSaved ? (
    <SolidHeartIcon colour={isProcessing ? 'light-foreground-alt' : 'favourite'} />
  ) : (
    <LineHeartIcon colour={isProcessing ? 'light-foreground-alt' : 'favourite'} />
  )

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      preventDefault(e)
      stopPropagation(e)

      onClick()
    },
    [onClick],
  )

  const iconOnlyButton = <IconBookmarkButton
    {...rest}
    className={clsx(className, getThemeClassName({ mobile: 'default' }), `withlabel-${withLabel}`)}
    shape="circle"
    kind={iconKind}
    variant={iconVariant}
    onClick={handleClick}
    disabled={isProcessing}
    aria-label={label}
    size={size ?? 'medium'}
    data-testid={clsx(testId, {
      'bookmark-button-saved': isSaved,
      'bookmark-button-unsaved': !isSaved,
    })}
  >
    {savedIcon}
  </IconBookmarkButton>

  const chipButton = <ChipBookmarkButton
    {...rest}
    className={clsx(className, getThemeClassName({ mobile: 'default' }), `withlabel-${withLabel}`)}
    variant="filled"
    size={size ?? 'small'}
    onClick={handleClick}
    startIcon={savedIcon}
    aria-label={label}
    disabled={isProcessing}
    data-testid={clsx(testId, {
      'bookmark-button-saved': isSaved,
      'bookmark-button-unsaved': !isSaved,
    })}
  >
    {label}
  </ChipBookmarkButton>

  return <>
    {withLabel === 'never' && iconOnlyButton}
    {withLabel === 'always' && chipButton}
    {withLabel === 'tablet' && <>
      {iconOnlyButton}
      {chipButton}
    </>}
  </>
}

function BaseBookmarkButton(props: Props) {
  const shouldShowTripPlannerSaveEntryPoints =
    useShouldShowTripPlannerSaveEntryPoints()

  if (!shouldShowTripPlannerSaveEntryPoints) {
    return null
  }

  return <BaseBookmarkButtonInner {...props} />
}

export default React.memo(BaseBookmarkButton)
